import { AvailableAppointmentTimesResponse } from "../../types";
import { ProvetApiClient } from "../../utilities";

/**
 * Parameters for fetchAvailableTimes function.
 */
export interface FetchAvailableTimesParams {
  api: ProvetApiClient;
  startDate: Date;
  endDate: Date;
  departmentId: number;
  reasonId: number;
  reasonDuration: number;
  bufferTime: number;
  includeFirstAvailableDate: boolean;
  abortSignal?: AbortSignal;
}

/**
 * Fetches available appointment times for a given interval and context.
 * @param params - Parameters for fetching available times
 * @returns Promise of available appointment times and the first later available date
 * @throws ApiError (or an AbortError) when the times can't be loaded, so a failure is never
 * shown as "no available times"
 */
export async function fetchAvailableTimes({
  api,
  startDate,
  endDate,
  departmentId,
  reasonId,
  reasonDuration,
  bufferTime,
  includeFirstAvailableDate,
  abortSignal
}: FetchAvailableTimesParams): Promise<AvailableAppointmentTimesResponse> {
  const now = new Date();
  let effectiveStartDate: Date;
  // Compare date parts only (ignore time)
  const startDateOnly = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (startDateOnly.getTime() > nowDateOnly.getTime()) {
    effectiveStartDate = startDate;
  } else {
    effectiveStartDate = new Date(now.getTime() + bufferTime * 60000);
  }
  const result = await api.fetchAvailableAppointmentTimes(
    effectiveStartDate,
    endDate,
    departmentId,
    reasonId,
    reasonDuration,
    bufferTime,
    includeFirstAvailableDate,
    abortSignal
  );
  return result || { availableTimes: [] };
}
